/* eslint-disable ember/no-computed-properties-in-native-classes */
import EmberObject, { computed } from '@ember/object';

import { A } from '@ember/array';
import * as ConversionUtils from '../utils/conversion';
import * as ResultsUtils from '../utils/results';

export default class CircuitResults extends EmberObject {
  circuit = null;
  actualStage = null;
  adcBandwidth = null;
  frequencyArray = null;
  whoAmI = 'app/models/results/circuit-results.js';

  //TODO: Need to rename this to something more descriptive
  //It's really an array (by frequency) of all the calculations you can do on an implementation
  @computed('circuit', 'frequencyArray') get filterAmplitude() {
    let circuit = this.circuit;
    let frequencyArray = this.frequencyArray;
    var supportedComponents = circuit.supportedComponents;
    var opAmp = circuit.opAmp;
    var actualFilterAmplitudeModel = circuit.actualFilterAmplitudeModel;
    var hash = { r1: null, r2: null, r3: null, r4: null, r5: null, c1: null, c2: null, c5: null, opAmp: opAmp };

    for (let i = 0; i < supportedComponents.length; i++) {
      var componentName = supportedComponents[i];
      var valuePath = componentName + '.value';
      var componentValue = circuit.get(valuePath);
      hash[componentName] = componentValue;
    }

    var result = frequencyArray.map((frequency) => {
      hash.frequency = frequency;
      var res = actualFilterAmplitudeModel.create(hash);
      return res;
    });

    return result;
  }

  @computed('filterAmplitude') get noiseFilterAmplitude() {
    return this.filterAmplitude;
  }
  @computed('filterAmplitude') get magnitude() {
    var filterAmplitude = this.filterAmplitude;
    var result = filterAmplitude.map((item) => {
      var res = item.magnitude;
      return res;
    });

    return result;
  }

  @computed('filterAmplitude') get phase() {
    var filterAmplitude = this.filterAmplitude;
    var phase = filterAmplitude.map((item) => {
      return item.phase;
    });

    let result = ResultsUtils.unwrapPhase(phase);
    return result;
  }

  @computed('circuit', 'actualStage') get idealOpAmp() {
    var circuit = this.circuit;
    var idealOpAmpValuePaths = circuit.idealOpAmpValuePaths;
    var optimization = circuit.optimization;
    var stageFp = circuit.stageFp;
    var gain = circuit.gain;
    var stageQ = circuit.stageQ;
    var idealOpAmpModel = circuit.idealOpAmpModel;
    var algorithm = circuit.algorithm;
    var hash = {
      r1: null,
      r2: null,
      r3: null,
      r4: null,
      r5: null,
      c1: null,
      c2: null,
      c5: null,
      q0: null,
      k: null,
      optimization: optimization,
      stageFp: stageFp,
      gain: gain,
      stageQ: stageQ,
      algorithm: algorithm,
    };
    for (let i = 0; i < idealOpAmpValuePaths.length; i++) {
      var componentName = idealOpAmpValuePaths[i];
      var valuePath = componentName + '.value';
      var componentValue = circuit.get(valuePath);
      hash[componentName] = componentValue;
    }

    return idealOpAmpModel.create(hash);
  }

  @computed('adcBandwidth', 'circuit', 'filterAmplitude', 'frequencyArray', 'magnitude') get noiseGroup() {
    var circuit = this.circuit;
    let supportedComponents = circuit.supportedComponents;
    let opAmp = circuit.opAmp;
    let magnitude = this.magnitude;
    let frequencyArray = this.frequencyArray;
    let adcBandwidth = this.adcBandwidth;
    let filterAmplitude = this.filterAmplitude;
    let hash = {
      r1: null,
      r2: null,
      r3: null,
      r4: null,
      r5: null,
      c1: null,
      c2: null,
      c5: null,
      opAmp: opAmp,
      magnitude: magnitude,
      frequencyArray: frequencyArray,
      adcBandwidth: adcBandwidth,
      filterAmplitude: filterAmplitude,
    };

    for (let j = 0; j < supportedComponents.length; j++) {
      let componentName = supportedComponents[j];
      let valuePath = componentName + '.value';
      let componentValue = circuit.get(valuePath);
      hash[componentName] = componentValue;
    }

    let result = circuit.noiseGroupModel.create(hash);
    return result;
  }

  @computed('circuit', 'actualStage') get stepResponse() {
    var circuit = this.circuit;
    var actualStage = this.actualStage;
    var implementation = actualStage.implementation;
    var timeAxis = actualStage.timeAxis;
    var idealStep = actualStage.stepResponseIdeal;
    var result = implementation.getStepValues(idealStep, circuit, timeAxis);
    return result;
  }

  @computed('stepResponse') get impulseResponse() {
    var stepResponse = this.stepResponse;
    var impulseValues = [stepResponse[0]];
    for (var i = 1; i < stepResponse.length; i++) {
      var val = stepResponse[i] - stepResponse[i - 1];
      impulseValues.push(val);
    }

    return impulseValues;
  }

  @computed('filterAmplitude') get inputImpedance() {
    var filterAmplitude = this.filterAmplitude;
    var result = filterAmplitude.map((item) => {
      var res = item.inputImpedance;
      return res;
    });

    return result;
  }

  @computed('inputImpedance') get inputImpedanceMagnitude() {
    var inputImpedance = this.inputImpedance;
    var result = inputImpedance.map((item) => {
      return item.magnitude();
    });

    return result;
  }

  @computed('inputImpedance') get inputImpedancePhase() {
    var inputImpedance = this.inputImpedance;
    var phaseArray = inputImpedance.map((item) => {
      return Math.atan2(item.imaginary, item.real);
    });

    var result = ResultsUtils.unwrapPhase(phaseArray);
    result = ConversionUtils.radianArrayToDegreeArray(result);
    return result;
  }

  @computed('actualStage', 'frequencyArray', 'circuit', 'magnitude') get stageValues() {
    let circuit = this.circuit;
    let frequencyArray = this.frequencyArray;

    var actualStage = this.actualStage;
    var loopFp = circuit.stageFp;
    var loopQ = circuit.stageQ;
    var gain = circuit.gain;
    var magnitude = this.magnitude;
    var fpArray = actualStage.getActualFpArray(loopFp);
    var qArray = actualStage.getActualQArray(loopQ);
    var gainArray = actualStage.getActualGainArray(gain);

    var fpQGainErrorObj = A();
    for (var j = 0; j < fpArray.length; j++) {
      fpQGainErrorObj[j] = { fp: fpArray[j], q: qArray[j], gain: gainArray[j], error: undefined };
      fpQGainErrorObj[j].error = this._calculatePointError(
        frequencyArray,
        { fp: fpArray[j], q: qArray[j], gain: gainArray[j] },
        magnitude,
      );
    }

    fpQGainErrorObj = fpQGainErrorObj.sortBy('error');
    var result = this._getActualFpAndQ(frequencyArray, fpQGainErrorObj, magnitude);
    return result;
  }

  _getActualFpAndQ(newFrequencyPoints, fpQGainErrorObjIn, componentMagnitudes) {
    var stdDev;
    var count = 0;
    var fpQGainErrorObj = fpQGainErrorObjIn;
    var [alpha, gamma, rho, sigma] = [1, 2, 0.5, 0.5]; // Reflection, expansion, contraction and shrink coefficients, respectively

    do {
      // Finding the average of fpArray, except for last point
      var centroid = this._computeAveFpGainAndQ(fpQGainErrorObj);
      var arrayLength = fpQGainErrorObj.length;

      var reflectedPt = this._actualStageSpecReflection(
        newFrequencyPoints,
        fpQGainErrorObj,
        componentMagnitudes,
        centroid,
        alpha,
      );
      if (reflectedPt.error < fpQGainErrorObj[0].error) {
        // Reflected point is best so far
        var expandedPt = this._actualStageSpecExpansion(
          newFrequencyPoints,
          componentMagnitudes,
          centroid,
          reflectedPt,
          gamma,
        );
        if (expandedPt.error < reflectedPt.error) {
          // Expanded point is even better
          this._pointCopy(expandedPt, fpQGainErrorObj[arrayLength - 1]);
        } else {
          this._pointCopy(reflectedPt, fpQGainErrorObj[arrayLength - 1]);
        }
      }
      // Reflected point is not better than the second worst point
      else if (reflectedPt.error >= fpQGainErrorObj[arrayLength - 2].error) {
        if (reflectedPt.error < fpQGainErrorObj[arrayLength - 1].error) {
          // Reflected point is better than worst point
          let contractedPt = this._actualStageSpecContraction(
            newFrequencyPoints,
            fpQGainErrorObj,
            componentMagnitudes,
            centroid,
            reflectedPt,
            rho,
            'out',
          );
          if (contractedPt.error < reflectedPt.error) {
            // Contracted point is even better
            this._pointCopy(contractedPt, fpQGainErrorObj[arrayLength - 1]);
          } else {
            this._actualStageSpecShrink(newFrequencyPoints, fpQGainErrorObj, componentMagnitudes, sigma);
          }
        } else {
          // Reflected point is not better than worst point
          let contractedPt = this._actualStageSpecContraction(
            newFrequencyPoints,
            fpQGainErrorObj,
            componentMagnitudes,
            centroid,
            reflectedPt,
            rho,
            'in',
          );
          if (contractedPt.error < fpQGainErrorObj[arrayLength - 1].error) {
            // Contracted point is better than worst point
            this._pointCopy(contractedPt, fpQGainErrorObj[arrayLength - 1]);
          } else {
            this._actualStageSpecShrink(newFrequencyPoints, fpQGainErrorObj, componentMagnitudes, sigma);
          }
        }
      } else {
        // Reflected point is not better than some of the rest but is better than second worst point
        this._pointCopy(reflectedPt, fpQGainErrorObj[arrayLength - 1]);
      }

      fpQGainErrorObj = A(fpQGainErrorObj).sortBy('error');
      stdDev = this._computeStdDev(fpQGainErrorObj);
      count++;
    } while (!(stdDev < 1e-8 || count === 1000 || fpQGainErrorObj[0].error < 1e-6)); // Algorithm usually stops after std dev threshold is met

    var result = { fp: undefined, q: undefined, gain: undefined };
    this._pointCopy(fpQGainErrorObj[0], result);

    return result;
  }

  _pointCopy(srcPt, destPt) {
    [destPt.fp, destPt.q, destPt.gain] = [srcPt.fp, srcPt.q, srcPt.gain];
    if (Object.hasOwn(destPt, 'error')) {
      destPt.error = srcPt.error;
    }
  }

  _computeStdDev(fpQGainErrorObj) {
    var [sum, sumOfSqs, arrayLength] = [0, 0, fpQGainErrorObj.length];

    fpQGainErrorObj.forEach((pt) => {
      sum += pt.error;
      sumOfSqs += pt.error * pt.error;
    });

    return Math.sqrt((sumOfSqs - (sum * sum) / arrayLength) / (arrayLength - 1));
  }

  _computeAveFpGainAndQ(fpQGainErrorObj) {
    var [sumFp, sumQ, sumGain, arrayLength] = [0, 0, 0, fpQGainErrorObj.length];

    for (var i = 0; i < arrayLength - 1; i++) {
      sumFp += fpQGainErrorObj[i].fp;
      sumQ += Number(fpQGainErrorObj[i].q);
      sumGain += fpQGainErrorObj[i].gain;
    }

    var centroid = {
      fp: sumFp / (arrayLength - 1),
      q: sumQ / (arrayLength - 1),
      gain: sumGain / (arrayLength - 1),
    };

    return centroid;
  }

  _actualStageSpecReflection(newFrequencyPoints, fpQGainErrorObj, componentMagnitudes, centroid, scalar) {
    var arrayLength = fpQGainErrorObj.length;
    var reflectedPt = {
      fp: centroid.fp + scalar * (centroid.fp - fpQGainErrorObj[arrayLength - 1].fp),
      q: centroid.q + scalar * (centroid.q - Number(fpQGainErrorObj[arrayLength - 1].q)),
      gain: centroid.gain + scalar * (centroid.gain - fpQGainErrorObj[arrayLength - 1].gain),
      error: undefined,
    };
    reflectedPt.error = this._calculatePointError(newFrequencyPoints, reflectedPt, componentMagnitudes);

    return reflectedPt;
  }

  _actualStageSpecExpansion(newFrequencyPoints, componentMagnitudes, centroid, reflectedPt, scalar) {
    var expandedPt = {
      fp: centroid.fp + scalar * (reflectedPt.fp - centroid.fp),
      q: centroid.q + scalar * (reflectedPt.q - centroid.q),
      gain: centroid.gain + scalar * (reflectedPt.gain - centroid.gain),
      error: undefined,
    };
    expandedPt.error = this._calculatePointError(newFrequencyPoints, expandedPt, componentMagnitudes);

    return expandedPt;
  }

  _actualStageSpecContraction(
    newFrequencyPoints,
    fpQGainErrorObj,
    componentMagnitudes,
    centroid,
    reflectedPt,
    scalar,
    inOrOut,
  ) {
    var contractedPt = {
      fp: centroid.fp,
      q: centroid.q,
      gain: centroid.gain,
      error: undefined,
    };

    if (inOrOut === 'in') {
      // Reflected point is not better than worst point
      var arrayLength = fpQGainErrorObj.length;
      contractedPt.fp += scalar * (fpQGainErrorObj[arrayLength - 1].fp - centroid.fp);
      contractedPt.q += scalar * (fpQGainErrorObj[arrayLength - 1].q - centroid.q);
      contractedPt.gain += scalar * (fpQGainErrorObj[arrayLength - 1].gain - centroid.gain);
    } else if (inOrOut === 'out') {
      // Reflected point is better than worst point
      contractedPt.fp += scalar * (reflectedPt.fp - centroid.fp);
      contractedPt.q += scalar * (reflectedPt.q - centroid.q);
      contractedPt.gain += scalar * (reflectedPt.gain - centroid.gain);
    }

    contractedPt.error = this._calculatePointError(newFrequencyPoints, contractedPt, componentMagnitudes);

    return contractedPt;
  }

  _actualStageSpecShrink(newFrequencyPoints, fpQGainErrorObj, componentMagnitudes, scalar) {
    var arrayLength = fpQGainErrorObj.length;
    for (var j = 1; j < arrayLength; j++) {
      fpQGainErrorObj[j].fp = fpQGainErrorObj[0].fp + scalar * (fpQGainErrorObj[j].fp - fpQGainErrorObj[0].fp);
      fpQGainErrorObj[j].q =
        Number(fpQGainErrorObj[0].q) + scalar * (Number(fpQGainErrorObj[j].q) - Number(fpQGainErrorObj[0].q));
      fpQGainErrorObj[j].gain = fpQGainErrorObj[0].gain + scalar * (fpQGainErrorObj[j].gain - fpQGainErrorObj[0].gain);
      fpQGainErrorObj[j].error = this._calculatePointError(
        newFrequencyPoints,
        { fp: fpQGainErrorObj[j].fp, q: fpQGainErrorObj[j].q, gain: fpQGainErrorObj[j].gain },
        componentMagnitudes,
      );
    }
  }

  _calculatePointError(freqArray, point, componentMagnitudes) {
    // var stageMagnitude = freqArray.map(item => {}
    //     orderStage = [],
    //     error,
    //     idealMagnitude = [],
    //     stage = [],
    //     difference;
    var idealMagnitude = [];

    if (point.fp < 0 || point.q < 0 || point.gain < 0) {
      var stageMagnitude = freqArray.map(() => {
        return 0;
      });
      if (point.q < 0) {
        point.q = 0;
      }

      if (point.fp < 0) {
        point.fp = 0;
      }

      if (point.gain < 0) {
        point.gain = 0;
      }

      idealMagnitude = stageMagnitude;
    } else {
      idealMagnitude = this.actualStage.targetStage
        .createAmplitude(point.fp, point.q, point.gain, freqArray)
        .map((complex) => {
          return complex.magnitude();
        });
    }

    var error = 0;

    //for each point in ideal magnitude
    for (var k = 0; k < idealMagnitude.length; k++) {
      var difference = componentMagnitudes[k] - idealMagnitude[k];
      error = error + difference * difference; // changed from error[k] on lhs to error
    }

    return error;
  }
}
